import {
  Component,
  EventEmitter,
  Input,
  Output,
  SimpleChanges,
} from "@angular/core";
import { SavedFilter } from "../../../models/filter.model";
import { IUser } from "../../../models/user.model";
import { MessageService } from "primeng/api";
import { FilterService } from "../../../services/filter.service";
import { IPanel } from "../../../util/common.models";
import { NotificationSharedService } from "../../../notification/notifications-shared.service";
import { filterKeys } from "../../../constants/filters.constants";
import { TEXTS } from "../../../constants/common.constants";
import { take } from "rxjs";

@Component({
  selector: "app-saved-filter-list",
  templateUrl: "./saved-filter-list.component.html",
  styleUrl: "./saved-filter-list.component.scss",
})
export class SavedFilterListComponent {
  @Input() sidebarVisible: boolean = false;
  @Input() filters: SavedFilter[] = [];
  @Input() currentUser: IUser | null = null;
  @Input() styleClass: string = "";
  @Input() setEdit: boolean = false;
  @Input() loading: boolean = false;
  @Input() defaultFilterId: number | null = null;
  @Input() appliedFilter: SavedFilter | null = null;
  @Input() panels!: IPanel[];

  @Output() applyFilter = new EventEmitter<any>();
  @Output() saveFilter = new EventEmitter<any>();
  @Output() editFilter = new EventEmitter<any>();
  @Output() deleteFilter = new EventEmitter<any>();
  @Output() setAsDefaultFilterAndApply = new EventEmitter<any>();
  @Output() removeDefaultFilterAndApply = new EventEmitter<any>();

  edit = false;
  visible: boolean = false;
  toBeDeleted: SavedFilter | null = null;
  filterName: string = "";
  markPrivateSharedFilterModal = false;
  filterInFocus: any;
  sharedPrivateFilterNotes = TEXTS.sharedPrivateFilterNotesText;
  markPrivateFilterModalText = TEXTS.markPrivateFilterModalText;
  markSharedFilterModalText = TEXTS.markSharedFilterModalText;
  filterModalText = "";

  constructor(
    private readonly messageService: MessageService,
    public filterService: FilterService,
    private notificationSharedService: NotificationSharedService
  ) {}

  ngOnInit() {
    if (this.setEdit) this.edit = this.setEdit;
  }

  ngOnChanges(changes: SimpleChanges): void {
    this.filters.forEach((f) => {
      f.isEditable = this.isEditable(f);
    });
    if (changes["filters"]) {
      const previousValue = changes["filters"].previousValue;
      const currentValue = changes["filters"].currentValue;
      const firstChange = changes["filters"].firstChange;
      if (currentValue && currentValue !== previousValue && !firstChange) {
        currentValue.forEach((cv: SavedFilter) => {
          if (cv.isEditing) {
            this.focusOnElementById("" + cv.id);
          }
        });
      }
    }

    this.filters.sort((a, b) => {
      if (a.name && b.name) return a.name.localeCompare(b.name);
      return 0;
    });

    if (!this.sidebarVisible) {
      this.filters = this.filters.filter((f) => f.id);
    }
  }

  focusOnElementById(elementId: string) {
    try {
      setTimeout(() => {
        const element = document.getElementById(elementId);
        if (element) {
          element.focus();
          element.scrollIntoView({ behavior: "smooth", block: "center" });
        }
      }, 0);
    } catch (error) {
      console.error("Element not found:", error);
    }
  }

  getDateFromTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleDateString();
  }

  getTimeFromTimestamp(timestamp: string): string {
    const date = new Date(timestamp);
    return date.toLocaleTimeString();
  }

  isEditable(filter: SavedFilter) {
    if (filter.user) {
      return this.currentUser?.email === filter.user.email;
    } else {
      return true;
    }
  }

  getToolTip(filter: SavedFilter) {
    let timeStamp = filter.updated_at ?? "";
    if (filter?.filterValues?.length) {
      timeStamp = filter.filterValues[0].updated_at ?? "";
    }

    let name = filter.user?.email;
    if (filter.user?.first_name) {
      name = filter.user?.first_name + " " + filter.user?.last_name;
    }
    return (
      "Updated by " +
      name +
      " on " +
      this.getDateFromTimestamp(timeStamp) +
      " at " +
      this.getTimeFromTimestamp(timeStamp)
    );
  }

  onApplyFilter(filter: SavedFilter) {
    this.applyPanelSelectionFromFilter(filter);
    this.applyFilter.emit(filter);
  }

  onApplyAndSetDefaultFilter(filter: SavedFilter) {
    this.defaultFilterId = filter.id!;
    this.applyPanelSelectionFromFilter(filter);
    this.setAsDefaultFilterAndApply.emit(filter);
  }

  private applyPanelSelectionFromFilter(filter: SavedFilter) {
    const panelFilter = filter?.filterValues?.find((filter: any) => {
      return filter.key == filterKeys.PANEL;
    });

    if (panelFilter) {
      const selectedPanels = panelFilter.value
        .split(",")
        .filter((filter: any) =>
          this.panels.map((item) => item.value).includes(filter)
        );
      this.notificationSharedService.updatedSelectedPanelsOnHome(
        selectedPanels
      );
    }
  }

  removeDefaultFilter(filter: SavedFilter) {
    this.defaultFilterId = null;
    this.deleteDefaultFilter();
    this.removeDefaultFilterAndApply.emit(filter);
  }

  deleteDefaultFilter() {
    const temp = this.defaultFilterId;
    this.filterService.deleteDefaultFilter().subscribe({
      next: (res) => {},
      error: (err) => {
        this.defaultFilterId = temp;
      },
    });
  }

  handleRemovedChip(
    $event: { key: string; value: string },
    filter: SavedFilter
  ) {
    filter.filterValues = filter.filterValues.filter(
      (f) => f.key !== $event.key && f.value !== $event.value
    );
  }

  updateFilter(filter: SavedFilter) {
    this.filters = this.filters.map((f) => {
      if (f.id == filter.id) {
        return { ...filter };
      }
      return { ...f };
    });
  }

  onEdit(filter: SavedFilter) {
    this.filters = this.filters
      .map((f) => {
        if (f.id == filter.id) {
          return { ...f, isEditing: true };
        }
        return { ...f, isEditing: false };
      })
      .filter((f) => f.id);
    this.editFilter.emit(filter);
  }

  onSave(filter: SavedFilter) {
    this.filters = this.filters.map((f) => {
      return { ...f, isEditing: false };
    });
    this.saveFilter.emit(filter);
  }

  onDelete(filter: SavedFilter) {
    this.toBeDeleted = filter;
    this.visible = true;
  }

  onClose() {
    this.filters = this.filters.map((f) => {
      return { ...f, isEditing: false };
    });
  }

  public close() {
    this.onClose();
  }

  handleConfirmDelete() {
    this.visible = false;
    this.deleteDefaultFilter();
    this.deleteFilter.emit(this.toBeDeleted);
    this.toBeDeleted = null;
  }
  handleCancelDelete() {
    this.visible = false;
    this.toBeDeleted = null;
  }

  updateExistingFilterSetSharedPrivate(newValue: any, filter: any): void {
    this.filterModalText = newValue
      ? this.markPrivateFilterModalText
      : this.markSharedFilterModalText;

    this.filterInFocus = filter;
    this.filterInFocus.privateFilter = newValue;
    this.markPrivateSharedFilterModal = true;

    this.filters = this.filters.map((filter) => {
      if (filter.id == this.filterInFocus.id) {
        filter.privateFilter = newValue;
      }

      return filter;
    });
  }

  onMarkPrivateSharedFilterCancel() {
    this.filters = this.filters.map((filter) => {
      if (filter.id == this.filterInFocus.id) {
        filter.privateFilter = !this.filterInFocus.privateFilter;
      }

      return filter;
    });

    this.filterInFocus = null;
  }

  onMarkPrivateSharedFilterConfirm() {
    this.filterService
      .updateFilterPrivacy(
        this.filterInFocus.id,
        this.filterInFocus.privateFilter
      )
      .pipe(take(1))
      .subscribe({
        next: () => {
          this.messageService.add({
            severity: "success",
            summary: "Success",
            detail: `${
              this.filterInFocus.privateFilter
                ? TEXTS.filterVisibilityApiPrivateSuccessMessage
                : TEXTS.filterVisibilityApiSharedSuccessMessage
            }`,
          });
        },
        error: (error) => {
          this.onMarkPrivateSharedFilterCancel();
        },
      });
  }
}
